// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import type { Facility, Project, WorkOrderPaginated } from '@evolvemep/api-ts';
import { Button } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { hasLengthExactly, type RequireAtLeastOne } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { BendCreatorForm } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendCreatorForm';
import { CreateBendElementDetailsModal } from 'modules/Shop/EvolveFabrication/BendElementDetails/CreateBendElementDetailsModal';

import {
  useBendCreatorCartContext,
  BendCreatorCartContextProvider,
} from './FormComponents/Context/useBendCreatorCartContext';
import {
  BendCreatorFormContext,
  bendCreatorInitialValues,
  useBendCreatorFormContext,
} from './FormComponents/Context/useBendCreatorFormContext';
import {
  BendCreatorValidationContext,
  useBendCreatorValidationContext,
} from './FormComponents/Context/useBendCreatorValidationContext';

const BendCreatorNewBendForm = ({ onClose }: { onClose: () => void }) => {
  const { setValues } = useBendCreatorFormContext();
  const { resetLastValidation } = useBendCreatorValidationContext();
  const { selectedItem, onSelect, update, append } = useBendCreatorCartContext();
  return (
    <BendCreatorForm
      onClose={onClose}
      onSubmit={(bendData) => {
        if (isNotNil(selectedItem)) {
          update(selectedItem.draftId, bendData);
          onSelect(null);
        } else {
          append(bendData);
        }
        setValues(bendCreatorInitialValues);
        resetLastValidation();
      }}
    />
  );
};

type Props = {
  /** If provided, will control the Modal directly, hiding the "Bend creator" button */
  opened?: boolean;
  onClose?: () => void;
  onBendsCreated: () => void;
  selectedFacilities?: Facility[];
} & RequireAtLeastOne<{
  workOrder?: WorkOrderPaginated;
  selectedProjects?: Project[];
}>;

export const BendCreatorModal = ({ opened: parentOpened, onClose: parentOnClose, onBendsCreated, ...props }: Props) => {
  const [opened, setOpened] = useUncontrolled({
    value: parentOpened,
    onChange: parentOnClose,
    defaultValue: false,
  });

  const close = useCallback(() => setOpened(false), [setOpened]);
  /* Default bender profiles are set per facility. Bends land in the work order's facility when there is one;
   * otherwise a facility is only certain when exactly one is selected, so with several we skip the defaults. */
  const facilityId =
    props.workOrder?.facilityId ??
    (isNotNil(props.selectedFacilities) && hasLengthExactly(props.selectedFacilities, 1)
      ? props.selectedFacilities[0].facilityId
      : null);

  return (
    <>
      {isNil(parentOpened) && (
        <Button leftSection={<EvolveIcon icon="BendCreator" />} onClick={() => setOpened(true)} variant="gradient">
          Bend creator
        </Button>
      )}

      <WrappedModal
        title="Bend creator"
        opened={opened}
        onClose={close}
        hideHeader
        hideModalButtons
        bodyProps={{ p: 0, display: 'flex', flex: 1, style: { flexDirection: 'column' } }}
        fullScreen
      >
        <BendCreatorFormContext>
          <BendCreatorValidationContext facilityId={facilityId}>
            <BendCreatorCartContextProvider>
              <BendCreatorNewBendForm onClose={close} />
              <CreateBendElementDetailsModal
                onBendsCreated={() => {
                  onBendsCreated();
                  close();
                }}
                {...props}
              />
            </BendCreatorCartContextProvider>
          </BendCreatorValidationContext>
        </BendCreatorFormContext>
      </WrappedModal>
    </>
  );
};
